import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './AuctionInventoryList.css';
import { API_BASE_URL } from '../../config/constant';

const AuctionInventoryList = () => {
    const { auctionId } = useParams();
    const navigate = useNavigate();
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
    });

    useEffect(() => {
        fetchInventoryList();
    }, [auctionId]);

    const fetchInventoryList = async (page = 1) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/auction/auction-inventory-list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
                },
                body: JSON.stringify({
                    source: 'web',
                    auction_id: parseInt(auctionId),
                    page: page
                })
            });

            const result = await response.json();
            
            if (result.status === 200) {
                setInventory(result.data.inventory_list);
                setPagination(result.data.pagination);
            } else {
                toast.error('Failed to fetch auction inventory');
            }
        } catch (error) {
            console.error('Error fetching auction inventory:', error);
            toast.error('Failed to fetch auction inventory');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        fetchInventoryList(page);
    };

    const getPageNumbers = () => {
        const { page, totalPages } = pagination;
        const delta = 2; // Number of pages to show on each side
        const range = [];
        const rangeWithDots = [];
        let l;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
                range.push(i);
            }
        }

        range.forEach((i) => {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (i - l !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            l = i;
        });

        return rangeWithDots;
    };

    const handleBack = () => {
        navigate('/auction');
    };

    const handleViewDetails = (leadId) => {
        // Find inventory item to get dealer_id and pricing data
        const inventoryItem = inventory.find(item => item.lead_id === leadId);
        const dealerId = inventoryItem?.basic_details?.dealer_id;
        
        // Navigate to vehicle details page with dealer_id and pricing data in state
        navigate(`/auction/inventory/${leadId}/details`, {
            state: { 
                dealerId,
                dealPrice: inventoryItem?.basic_details?.deal_price,
                otoBasePrice: inventoryItem?.basic_details?.oto_base_price,
                winningPrice: inventoryItem?.last_bid_amount,
                bidCount: inventoryItem?.bid_count,
                bidderCount: inventoryItem?.bidder_count,
                visitCount: inventoryItem?.visit_count,
            }
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            maximumFractionDigits: 0
        }).format(price);
    };

    const toCamelCase = (str) => {
        if (!str) return '';
        return str.toLowerCase()
                 .replace(/_/g, ' ')
                 .replace(/\b\w/g, char => char.toUpperCase());
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'BID_RECEIVED':
                return 'status-bid-received';
            case 'WON':
                return 'status-won';
            case 'LOST':
                return 'status-lost';
            case 'LIVE':
                return 'status-live';
            default:
                return 'status-default';
        }
    };

    const getDealerAuctionStatusColor = (status) => {
        switch (status) {
            case 'won':
                return 'status-won';
            case 'outbid':
                return 'status-outbid';
            case 'active':
                return 'status-active';
            default:
                return 'status-default';
        }
    };

    if (loading && inventory.length === 0) {
        return (
            <div className="auction-inventory-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading auction inventory...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="auction-inventory-container">
            {/* Header */}
            <div className="inventory-header">
                <div className="header-left">
                    <div className="arrow-bck" onClick={handleBack}>
                   <i className='ic-arrow_back'></i>
                 </div>
                    <h1>Auction Inventory</h1>
                    <p className="auction-id">Auction ID: {auctionId}</p>
                </div>
            </div>

            {/* Table */}
            <div className="inventory-table-section">
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Id</th>
                                <th>MMVY/<br/>Plate Number</th>
                                <th>Seller Dealer</th>
                                <th>Buyer Dealer</th>
                                <th>Deal Price</th>
                                <th>Won Price</th>
                                <th>No of bids</th>
                                <th>No of Bidder</th>
                                <th>Status</th>
                                <th>Visit Count</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {inventory.length === 0 ? (
                                <tr>
                                    <td colSpan="12" className="no-data">
                                        {loading ? 'Loading...' : 'No inventory data available'}
                                    </td>
                                </tr>
                            ) : (
                                inventory.map((item, index) => (
                                    <tr key={item.lead_id}>
                                        <td>{item.lead_id}</td>
                                        <td>
                                            <div className="vehicle-info">
                                                <div className="make-model">
                                                    {item.make} {item.model}
                                                </div>
                                                <div className="variant-year">
                                                    {item.variant} ({item.make_year})
                                                </div>
                                                <div className="plate-number">
                                                    {item.basic_details?.plate_number || '-'}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{item.seller_dealer_name || '-'}</td>
                                        <td>{item.buyer_dealer_name || '-'}</td>
                                        <td>{formatPrice(item.base_price)}</td>
                                        <td>{item.last_bid_amount ? formatPrice(item.last_bid_amount) : '-'}</td>
                                        <td>{item.bid_count || '-'}</td>
                                        <td>{item.bidder_count || '-'}</td>
                                        <td>{toCamelCase(item.auction_status) || '-'}</td>
                                        <td>{item.visit_count || '-'}</td>
                                        <td>
                                            <button 
                                                className="btn-line"
                                                onClick={() => handleViewDetails(item.lead_id)}
                                            >
                                                View 
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {true && (
                <div className="pagination-section">
                    <div className="pagination-container">
                        <button 
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={!pagination.hasPrev || loading}
                        >
                            Previous
                        </button>
                        <div className="page-numbers">
                            {getPageNumbers().map((pageNum, index) => (
                                <button
                                    key={index}
                                    className={`page-number ${pageNum === pagination.page ? 'active' : ''}`}
                                    onClick={() => typeof pageNum === 'number' ? handlePageChange(pageNum) : null}
                                    disabled={typeof pageNum !== 'number' || loading}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>
                        <button 
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={!pagination.hasNext || loading}
                        >
                            Next
                        </button>
                    </div>
                    <div className="pagination-info">
                        <span>Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuctionInventoryList;
